// Client-side Outlook email signature builder for Saksham Interiors.
// Everything here produces table-based, inline-CSS HTML that survives Outlook.

export const LOGO_URL =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_signature-forge-9/artifacts/dumow5kq_ChatGPT%20Image%20Sep%2024%2C%202026%2C%2011_56_00%20AM.png";

export const DIVIDER_URL =
  "https://static.prod-images.emergentagent.com/jobs/3ed28240-a578-49e0-8098-03c540c46d91/images/08fc1ffeb75f998f46881a78fa3955c459c5f589ab2ee6ba43ed9812d248cdce.jpeg";

export const FONT_STACK = "Aptos, Arial, Helvetica, sans-serif";

// Fixed company data. Addresses is a list so a future version can offer a
// dropdown of offices without touching the rest of the app.
export const COMPANY = {
  name: "Saksham Interiors Private Limited",
  office: "+91 80 4224 7140",
  officeHref: "tel:+918042247140",
  website: "sakshaminteriors.in",
  websiteHref: "https://sakshaminteriors.in",
};

export const OFFICE_ADDRESSES = [
  {
    id: "bengaluru-hebbal",
    label: "Bengaluru — Hebbal Kempapura",
    lines: [
      "No 15 & 16, Ground Floor,",
      "18th \u201CA\u201D Cross, Dasarahalli Main Road,",
      "Bhuvaneshwarinagara, Hebbal Kempapura,",
      "Bengaluru-560 024",
    ],
  },
];

const BRAND = {
  name: "#0F172A",
  title: "#475569",
  company: "#0F172A",
  orange: "#F7941D",
  label: "#1479C9",
  value: "#333333",
  divider: "#CBD5E1",
};

const esc = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const telHref = (num = "") => `tel:${String(num).replace(/[^\d+]/g, "")}`;

// Optional HTML line-break opportunities work without email-client CSS support.
// Escape each piece separately; never insert these characters into link targets.
const wrapText = (text = "", chunkSize = 8) =>
  String(text)
    .split(/(\s+|[.@/\-])/)
    .map((part) =>
      (part.match(new RegExp(`.{1,${chunkSize}}`, "gu")) || [part])
        .map(esc)
        .join("&#8203;")
    )
    .join("&#8203;");

const label = (text) =>
  `<span style="font-family:${FONT_STACK};font-weight:bold;color:${BRAND.label};">${esc(
    text
  )}</span>`;

const link = (href, text, testId) =>
  `<a data-testid="${testId}" href="${esc(href)}" style="font-family:${FONT_STACK};font-weight:normal;color:${
    BRAND.value
  };text-decoration:none;">${wrapText(text)}</a>`;

// Builds the signature <table> block (what gets copied/pasted into Outlook).
export function buildSignatureTable(data = {}, address = OFFICE_ADDRESSES[0]) {
  const name = data.name || "";
  const title = data.title || "";
  const mobile = data.mobile || "";
  const mobile2 = data.mobile2 || "";
  const email = data.email || "";

  const addressHtml = (address?.lines || [])
    .map((line) => wrapText(line))
    .join("<br />");
  const rowStyle = `font-family:${FONT_STACK};font-size:13px;line-height:18px;color:${BRAND.value};padding:0 0 4px 0;`;
  const officeHtml = `${label("Office")} ${link(COMPANY.officeHref, COMPANY.office, "signature-office-link")}`;
  const phoneRows = mobile2
    ? `<tr><td data-testid="signature-mobile-row" style="${rowStyle}">${label("Mobile")} ${link(telHref(mobile), mobile, "signature-mobile-link")} / ${link(telHref(mobile2), mobile2, "signature-mobile-2-link")}</td></tr>
        <tr><td data-testid="signature-office-row" style="${rowStyle}">${officeHtml}</td></tr>`
    : `<tr><td data-testid="signature-mobile-row" style="${rowStyle}">${label("Mobile")} ${link(telHref(mobile), mobile, "signature-mobile-link")} <span style="font-family:${FONT_STACK};color:${BRAND.divider};">|</span> ${officeHtml}</td></tr>`;

  return `<table data-testid="signature-table" role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background-color:#ffffff;font-family:${FONT_STACK};">
  <tr>
    <td data-testid="signature-logo-cell" width="105" valign="middle" style="width:105px;min-width:105px;padding:0 16px 0 0;">
      <img data-testid="signature-logo" src="${LOGO_URL}" alt="Saksham Interiors" width="105" style="display:block;border:0;outline:none;text-decoration:none;width:105px;min-width:105px;height:auto;" />
    </td>
    <td width="3" valign="top" style="width:3px;padding:0;font-size:0;line-height:0;">
      <img data-testid="signature-divider" src="${DIVIDER_URL}" alt="" width="3" height="214" style="display:block;border:0;outline:none;width:3px;height:214px;" />
    </td>
    <td data-testid="signature-text-cell" valign="top" style="padding:2px 0 2px 14px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td data-testid="signature-name" style="font-family:${FONT_STACK};font-size:23px;line-height:27px;font-weight:bold;color:${BRAND.name};padding:0 0 2px 0;">${wrapText(name, 5)}</td>
        </tr>
        <tr>
          <td data-testid="signature-title" style="font-family:${FONT_STACK};font-size:14px;line-height:18px;font-weight:normal;color:${BRAND.title};padding:0 0 8px 0;">${wrapText(title)}</td>
        </tr>
        <tr>
          <td data-testid="signature-company" style="font-family:${FONT_STACK};font-size:16px;line-height:20px;font-weight:600;color:${BRAND.company};padding:0 0 7px 0;">${wrapText(COMPANY.name)}</td>
        </tr>
        <tr>
          <td style="padding:0 0 11px 0;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              <tr>
                <td data-testid="signature-underline" height="3" style="height:3px;background-color:${BRAND.orange};font-size:0;line-height:0;">&nbsp;</td>
              </tr>
            </table>
          </td>
        </tr>
        ${phoneRows}
        <tr>
          <td data-testid="signature-email-row" style="${rowStyle}">${label("Email")} ${link(`mailto:${email}`, email, "signature-email-link")}</td>
        </tr>
        <tr>
          <td data-testid="signature-web-row" style="${rowStyle}">${label("Web")} ${link(COMPANY.websiteHref, COMPANY.website, "signature-web-link")}</td>
        </tr>
        <tr>
          <td data-testid="signature-address-row" valign="top" style="font-family:${FONT_STACK};font-size:13px;line-height:18px;color:${BRAND.value};padding:3px 0 0 0;">${label("Address")} <span style="font-family:${FONT_STACK};color:${BRAND.value};">${addressHtml}</span></td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

// Full standalone document used by the Download HTML action.
export function buildStandaloneHtml(data = {}, address = OFFICE_ADDRESSES[0]) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(data.name || "Saksham")} — Email Signature</title>
</head>
<body style="margin:0;padding:24px;background-color:#ffffff;">
${buildSignatureTable(data, address)}
</body>
</html>`;
}

export function signatureFileName(name = "") {
  const cleaned = String(name)
    .trim()
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
  const base = cleaned || "Employee";
  return `${base}-Saksham-Signature.html`;
}
